// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PlusCircle, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AdminNcaaCalendarTable from "./AdminNcaaCalendarTable";
import AdminNcaaCalendarForm from "./AdminNcaaCalendarForm";
import AdminNcaaCalendarImport from "./AdminNcaaCalendarImport";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface NcaaCalendarEvent {
  id: string;
  title: string;
  description: string | null;
  category: string;
  start_date: string;
  end_date: string | null;
  division: string | null;
  source: string | null;
  sport_id?: string | null;
  created_at: string;
}

const AdminNcaaCalendarModule: React.FC = () => {
  const [events, setEvents] = useState<NcaaCalendarEvent[]>([]);
  const [totalEvents, setTotalEvents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<NcaaCalendarEvent | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [sportFilter, setSportFilter] = useState<string>("all");
  const [dateRangeFilter, setDateRangeFilter] = useState<{start?: string, end?: string}>({});
  
  const { toast } = useToast();

  useEffect(() => {
    fetchEvents();
  }, [currentPage, categoryFilter, sourceFilter, sportFilter, dateRangeFilter]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      console.log("🏈 NCAA Calendar Admin: Fetching events...");
      
      const from = (currentPage - 1) * itemsPerPage;
      const to = from + itemsPerPage - 1;
      
      // Updated query to include ALL admin-created events regardless of source
      // This will show NCAA events (source='ncaa') AND admin-created custom events AND other admin sources
      let query = supabase
        .from('calendar_events')
        .select('*', { count: 'exact' })
        .is('profile_id', null)
        .order('start_date', { ascending: true });

      // Apply category filter
      if (categoryFilter !== 'all') {
        query = query.eq('category', categoryFilter);
      }

      // Apply source filter
      if (sourceFilter !== 'all') {
        query = query.eq('source', sourceFilter);
      }

      // Apply sport filter
      if (sportFilter === 'none') {
        query = query.is('sport_id', null);
      } else if (sportFilter !== 'all') {
        query = query.eq('sport_id', sportFilter);
      }

      // Apply date range filter
      if (dateRangeFilter.start) {
        query = query.gte('start_date', dateRangeFilter.start);
      }
      if (dateRangeFilter.end) {
        query = query.lte('start_date', dateRangeFilter.end);
      }

      // Apply pagination
      query = query.range(from, to);

      const { data, error, count } = await query;
      
      if (error) {
        console.error("🏈 NCAA Calendar Admin: Error fetching events:", error);
        throw error;
      }
      
      console.log("🏈 NCAA Calendar Admin: Loaded", data?.length || 0, "events");
      setEvents(data || []);
      setTotalEvents(count || 0);
      
    } catch (error) {
      console.error('🏈 NCAA Calendar Admin: Error fetching events:', error);
      toast({
        title: 'Error',
        description: `Failed to load calendar events: ${error.message || 'Unknown error'}`,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEventSaved = () => {
    setIsFormModalOpen(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const handleEventDeleted = () => {
    fetchEvents();
  };

  const handleImportComplete = () => {
    setIsImportModalOpen(false);
    fetchEvents();
  };

  const handleEdit = (event: NcaaCalendarEvent) => {
    setEditingEvent(event);
    setIsFormModalOpen(true);
  };

  const totalPages = Math.ceil(totalEvents / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalEvents);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-semibold">Calendar Events</h2>
          <span className="text-sm text-gray-500">
            ({totalEvents > 0 ? `${startIndex}–${endIndex} of ${totalEvents}` : '0'} events)
          </span>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={isImportModalOpen} onOpenChange={setIsImportModalOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Upload className="h-4 w-4 mr-2" />
                Import CSV
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Import Calendar Events</DialogTitle>
              </DialogHeader>
              <AdminNcaaCalendarImport 
                onImportComplete={handleImportComplete}
                onClose={() => setIsImportModalOpen(false)}
              />
            </DialogContent>
          </Dialog>
          
          <Dialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingEvent(null); setIsFormModalOpen(true); }}>
                <PlusCircle className="h-4 w-4 mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                  {editingEvent ? 'Edit Calendar Event' : 'Add Calendar Event'}
                </DialogTitle>
              </DialogHeader>
              <AdminNcaaCalendarForm
                event={editingEvent}
                onEventSaved={handleEventSaved}
                onClose={() => setIsFormModalOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Calendar Management</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="events" className="w-full">
            <TabsList>
              <TabsTrigger value="events">Events</TabsTrigger>
            </TabsList>
            
            <TabsContent value="events" className="space-y-4">
              <AdminNcaaCalendarTable
                events={events}
                loading={loading}
                totalPages={totalPages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                onEdit={handleEdit}
                onEventDeleted={handleEventDeleted}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          sourceFilter={sourceFilter}
          onSourceFilterChange={setSourceFilter}
          sportFilter={sportFilter}
          onSportFilterChange={setSportFilter}
          dateRangeFilter={dateRangeFilter}
          onDateRangeFilterChange={setDateRangeFilter}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNcaaCalendarModule;
